import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import area from '@turf/area';
import centerOfMass from '@turf/center-of-mass';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import useMap from '../../../../hooks/useMap';

import API from '../../../../utils/api-map';
import olc from '../../../../utils/openlocal';
import { initIPFS } from '../../../../state/ipfs';
import { sqmToHa } from '../../../../utils/helpers';
import { MAP_EVENTS } from '../../../../constans/constans';

import Loader from '../../../Loader';
import MapHeader from '../MapHeader';
import MapInfo from '../MapInfo';

const NftMap = ({ state, setState, setShow, setShowPrev, uploadedFile, setUploadedFile }) => {
  const hectaresLimit = 0.2;
  const initialDataPolygon = {
    region: '',
    regionShortCode: '',
    polygon: [],
    codePlus: null,
    square: 0,
    polygonCoordinate: [],
  };
  const translate = useIntl();
  const { tools, mapRef, utils: { centerCoordinates, moveMap, zoomIn } } = useMap({ polygon: true, trash: true });
  const [isLoading, setIsLoading] = useState(false);
  const [newPolygon, setNewPolygon] = useState(initialDataPolygon);
  const [warning, setWarning] = useState({ cause: '', message: '' });

  const updatePolygon = async (e) => {
    const inputPolygon = e.features[0];
    const allMapData = tools.draw.getAll();
    // If polygon is already exist
    if (allMapData.features.length > 1) {
      tools.draw.delete(inputPolygon.id);
      return;
    }
    const center = centerOfMass(inputPolygon).geometry.coordinates;
    const { placeName, shortCode } = await API.getRegionNameFromPosition(center);
    const areaData = area(inputPolygon);
    const roundedArea = sqmToHa(areaData, 3);

    setWarning(
      +roundedArea < hectaresLimit
        ? { cause: 'small-zone', message: translate.formatMessage({ id: 'wizardForm.step2.map.smallNftArea' }, { hectaresLimit }) }
        : { cause: '', message: '' },
    );
    setNewPolygon({
      region: placeName,
      regionShortCode: shortCode,
      polygon: [inputPolygon],
      polygonCoordinate: inputPolygon.geometry.coordinates,
      codePlus: olc.encode(center[1], center[0]),
      square: roundedArea,
      geoFile: inputPolygon.filename || '',
    });
  };

  const deletePolygon = () => {
    setWarning({ cause: '', message: '' });
    setNewPolygon(initialDataPolygon);
  };

  const addMapEventListeners = () => {
    tools.map.on(MAP_EVENTS.MOVE, moveMap);
    tools.map.on(MAP_EVENTS.CREATE, updatePolygon);
    tools.map.on(MAP_EVENTS.DELETE, deletePolygon);
    tools.map.on(MAP_EVENTS.UPDATE, updatePolygon);
  };

  const saveData = () => {
    const canvas = tools.map.getCanvas();
    canvas.toBlob(async (blob) => {
      try {
        const ipfs = await initIPFS();
        const res = await ipfs.add(blob);
        if (!res) throw new Error();

        setState({ ...newPolygon, cidScreenShot: `/ipfs/${res.path}` });
        setShow(false);
        setShowPrev(false);
        setIsLoading(false);
      } catch (e) {
        console.log(e);
      }
    });
  };

  const scaleBeforeSave = () => {
    setIsLoading(true);
    zoomIn(newPolygon.polygon[0]);
    if (tools.map.isZooming()) tools.map.once(MAP_EVENTS.ZOOM_END, saveData);
    else saveData();
  };

  const drawInitialData = () => {
    // If user upload new zone with file - delete prev zone from map and draw file zone
    if (uploadedFile) {
      tools.draw.deleteAll();
      tools.draw.add(uploadedFile.features[0]);
      zoomIn(uploadedFile.features[0]);
      updatePolygon(uploadedFile);
      return;
    }
    // If nft zone exist - add it to the map
    if (state?.polygon?.length) {
      tools.draw.add(state.polygon[0]);
      zoomIn(state.polygon[0]);
      return;
    }
    // If nft zone doesn't exist - scale view
    tools.map.flyTo({ center: [5, 34], zoom: 2 });
  };

  useEffect(() => {
    if (!tools) return;
    try {
      addMapEventListeners();
      drawInitialData();
    } catch (e) {
      console.log(e);
    }
    return () => setUploadedFile();
  }, [tools]);

  return (
    <div className="map">
      {isLoading && <div className="cropper__loader"><Loader /></div>}
      <div className="map__box">
        <MapHeader coordinates={centerCoordinates} handleClose={() => setShow(false)} />
        <div ref={mapRef} className="map__map" />
        <MapInfo
          data={newPolygon}
          handleClick={() => scaleBeforeSave()}
          viewType="nft-map"
          extra={{ warning }}
        />
      </div>
    </div>
  );
};

export default NftMap;
